import { Message } from '@justrelate/llm-conversation';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { ColorScheme, DisplayMode, UserInputSource } from '../Chatbot';
import { LlmConfig } from '../llm';
import { useTranslate } from '../translate';
import { useLocalStorage } from '../useLocalStorage';

import { ChatBody } from './ChatBody';
import { ChatFooter } from './ChatFooter';
import { ChatModal } from './ChatModal';
import { OptionsDropdown } from './OptionsDropdown';

export interface SystemMessage {
  content: string;
}

interface ChatRendererProps {
  config: LlmConfig | null;
  messages: Message[];
  processUserMessage: (userInput: string, source: UserInputSource) => void;
  startNewConversation: () => void;
  isLoading: boolean;
  systemMessage: SystemMessage | null;
  setSystemMessage: (message: SystemMessage | null) => void;
  isVisible: boolean;
  onHide?: () => void;
  showHistory?: boolean;
  defaultDisplayMode: DisplayMode;
  inline?: boolean;
  colorScheme: ColorScheme;
}

export function ChatRenderer({
  config,
  messages,
  processUserMessage,
  startNewConversation,
  isLoading,
  systemMessage,
  setSystemMessage,
  isVisible,
  onHide,
  showHistory,
  defaultDisplayMode,
  inline,
  colorScheme,
}: ChatRendererProps) {
  const translate = useTranslate();
  const [isOptionsPanelOpen, setOptionsPanelOpen] = useState(false);
  const [displayMode, setDisplayMode] =
    useState<DisplayMode>(defaultDisplayMode);

  const inputRef = useRef<HTMLSpanElement>(null);
  const chatLogRef = useRef<HTMLDivElement>(null);

  const resetConversation = useCallback(() => {
    setSystemMessage(null);
    startNewConversation();
    inputRef.current?.focus();
  }, [setSystemMessage, startNewConversation]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent | KeyboardEvent) => {
      if (event.metaKey && event.key === 'k') {
        // press Apple+K to start a fresh conversation
        resetConversation();
      }
    },
    [resetConversation],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (!chatLogRef.current) return;
    chatLogRef.current.scrollTo({
      top: chatLogRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages.length]);

  const [promptHistory, setPromptHistory] =
    useLocalStorage<string[]>('promptHistory');

  const placeholder = translate('placeholder');

  if (!config) {
    return (
      <ChatModal
        isVisible={isVisible}
        onHide={onHide}
        displayMode={displayMode}
        inline={inline}
        colorScheme={colorScheme}
      >
        <div className="jr-ai-assist-modal-body">
          <div className="jr-ai-assist-loader" />
        </div>
        <div className="jr-ai-assist-modal-footer" />
      </ChatModal>
    );
  }

  return (
    <ChatModal
      isVisible={isVisible}
      onHide={onHide}
      displayMode={displayMode}
      inline={inline}
      colorScheme={colorScheme}
      headerActions={
        <>
          <OptionsDropdown
            isOpen={isOptionsPanelOpen}
            helpMeGetStarted={helpMeGetStarted}
            toggleOptions={toggleOptions}
            resetConversation={resetConversation}
          />

          {!inline && (
            <button
              type="button"
              className="jr-ai-assist-button ghost"
              onClick={toggleMaximized}
            >
              <i
                className={`jr-ai-icon jr-ai-icon-arrow-${displayMode === 'floating' ? 'bigger' : 'smaller'}`}
              />
            </button>
          )}
        </>
      }
    >
      <ChatBody
        chatLogRef={chatLogRef}
        isLoading={isLoading}
        showHistory={showHistory}
        messages={messages}
        promptHistory={promptHistory}
        runPrompt={runPrompt}
        config={config}
        systemMessage={systemMessage}
      />

      <ChatFooter
        inputRef={inputRef}
        placeholder={placeholder}
        sendPrompt={sendPrompt}
        isLoading={isLoading}
      />
    </ChatModal>
  );

  function sendPrompt(text: string) {
    if (!text.trim()) return;

    const MAX_PROMPT_HISTORY = 50;

    const slicedPromptHistory = promptHistory
      ? promptHistory.slice(0, MAX_PROMPT_HISTORY)
      : [];

    const promptInHistory = slicedPromptHistory.some(
      (p) => p.toLowerCase() === text.toLowerCase(),
    );

    if (messages.length === 0 && !promptInHistory) {
      setPromptHistory([...slicedPromptHistory, text]);
    }

    runPrompt(text, 'freetext');
  }

  function runPrompt(prompt: string, source: UserInputSource) {
    setSystemMessage(null);
    processUserMessage(prompt, source);
  }

  function helpMeGetStarted() {
    toggleOptions();

    runPrompt(translate('helpPrompt'), 'suggestion');
  }

  function toggleMaximized() {
    setDisplayMode((prev) => (prev === 'overlay' ? 'floating' : 'overlay'));
  }

  function toggleOptions() {
    setOptionsPanelOpen((prev) => !prev);
  }
}
